/**
 * Partners: the attribution list, read only.
 *
 * This is the launch surface for Oasis. The CPA ledger exists in the database and is being
 * populated, and its UI is behind a flag that is off, so this page shows what was attributed
 * and why, plus the accrual rules in force, and nothing that implies a payable balance the
 * owner cannot yet see the workings of.
 *
 * Nothing on this page is customer data. An order id, a date, a status, the partner's own
 * subid and clickid, and an eligibility reason. No name, no email, no phone, no address, no
 * items, ever, because these rows are what a partner reconciles against.
 */
import { useState } from 'react';
import { Card, DataTable, EmptyState, ErrorState, MoneyText, Pill, Skeleton, type Column } from '@/components';
import { formatMoney } from '@/lib/format';
import { usePartnerAttribution, usePartners } from './api';
import type { PartnerAttributionRow, PartnerRow } from './types';
import '@/features/finance/finance.css';

export function PartnersPage() {
  const partners = usePartners();
  const [selected, setSelected] = useState<string | null>(null);
  const attribution = usePartnerAttribution(selected);

  if (partners.isLoading) return <Skeleton shape="card" />;
  if (partners.isError || !partners.data) {
    return (
      <ErrorState
        message="Partners could not be loaded."
        onRetry={() => {
          void partners.refetch();
        }}
      />
    );
  }

  return (
    <div className="o-stack">
      {partners.data.items.length === 0 ? (
        <Card title="Partners">
          <EmptyState message="No partners configured yet." />
        </Card>
      ) : (
        partners.data.items.map((partner) => (
          <PartnerCard
            key={partner.partner_id}
            partner={partner}
            active={selected === partner.slug}
            onSelect={() => setSelected(selected === partner.slug ? null : partner.slug)}
          />
        ))
      )}

      <Card
        title={`Attribution${selected ? ` · ${selected}` : ''}`}
        basis="ledger"
        action={<Pill tone="neutral">read only</Pill>}
      >
        <AttributionTable rows={attribution.data?.items ?? []} loading={attribution.isLoading} />
        <p className="o-muted">
          {attribution.data?.pii_policy ??
            'Partner surfaces carry an order id and timestamps only. Never a name, email, phone, address or item.'}
        </p>
        <p className="o-muted">
          The CPA ledger is populated and its screen is behind a flag that is off at launch, so this
          list is the whole partner surface for now.
        </p>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function PartnerCard({
  partner,
  active,
  onSelect,
}: {
  partner: PartnerRow;
  active: boolean;
  onSelect: () => void;
}) {
  const rule = partner.rules[0];
  return (
    <Card
      title={partner.display_name}
      basis="ledger"
      action={
        <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <Pill tone={partner.status === 'active' ? 'ok' : partner.status === 'paused' ? 'warn' : 'neutral'}>
            {partner.status}
          </Pill>
          <button type="button" className="o-chip" aria-pressed={active} onClick={onSelect}>
            {active ? 'Showing attribution' : 'Show attribution'}
          </button>
        </span>
      }
    >
      <div className="o-scorecard">
        <div className="o-kpi">
          <span className="o-kpi__label">Attributed orders</span>
          <span className="o-kpi__value">{partner.attributed_orders}</span>
          <span className="o-kpi__meta">{`cookie, ${partner.attribution_window_days} day window`}</span>
        </div>
        <div className="o-kpi">
          <span className="o-kpi__label">Eligible</span>
          <span className="o-kpi__value">{partner.eligible_orders}</span>
          <span className="o-kpi__meta">{`${partner.cooling_days} day cooling`}</span>
        </div>
        <div className="o-kpi">
          <span className="o-kpi__label">Accrued</span>
          <span className="o-kpi__value">{formatMoney(partner.accrued_cents, { showCents: false })}</span>
          <span className="o-kpi__meta">{`${formatMoney(partner.cpa_cents, { showCents: false })} per order`}</span>
        </div>
        <div className="o-kpi">
          <span className="o-kpi__label">Balance</span>
          <span className="o-kpi__value">{formatMoney(partner.balance_cents, { showCents: false })}</span>
          <span className="o-kpi__meta">{`${formatMoney(partner.paid_cents, { showCents: false })} paid`}</span>
        </div>
      </div>
      <p className="o-muted">
        {partner.new_customer_only
          ? 'First paid order only. Paying a partner CPA for a reorder from a customer the brand already owns is paying for retention twice.'
          : 'Every paid order accrues, including reorders from existing customers.'}
        {rule ? ` Rule in force since ${rule.effective_from}.` : ''}
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

export function AttributionTable({ rows, loading }: { rows: PartnerAttributionRow[]; loading: boolean }) {
  const columns: readonly Column<PartnerAttributionRow>[] = [
    { id: 'order', header: 'Order', priority: 1, mono: true, render: (r) => `#${r.order_id}` },
    { id: 'paid_on', header: 'Paid', priority: 1, mono: true, render: (r) => r.paid_on ?? <span className="o-muted">not paid</span> },
    {
      id: 'status',
      header: 'Status',
      priority: 1,
      render: (r) =>
        r.eligible ? (
          <Pill tone="ok">eligible</Pill>
        ) : (
          <Pill tone="neutral" title={r.ineligible_reason ?? undefined}>
            {r.ineligible_reason ?? 'pending'}
          </Pill>
        ),
    },
    {
      id: 'total',
      header: 'Order total',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => (r.order_total_cents === null ? '' : <MoneyText cents={r.order_total_cents} showCents />),
    },
    { id: 'subid', header: 'Subid', priority: 3, mono: true, render: (r) => r.subid ?? '' },
    { id: 'clickid', header: 'Clickid', priority: 5, mono: true, render: (r) => r.clickid ?? '' },
    { id: 'rule', header: 'Matched by', priority: 4, render: (r) => r.matched_rule },
    {
      id: 'first',
      header: 'First order',
      priority: 4,
      render: (r) => (r.is_first_order === null ? '' : r.is_first_order ? 'yes' : 'no'),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.attribution_id}
      caption="Orders attributed to a partner"
      loading={loading}
      emptyState={<EmptyState message="No attributed orders yet." />}
      allowHorizontalScroll
    />
  );
}
